import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Typography, Box, Modal, Backdrop, Fade,
  IconButton, TextField, InputAdornment, Stack, Pagination
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;
const POSTS_PER_PAGE = 10; // Number of posts per page

const modalStyle = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 900,
  bgcolor: '#121212',
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
  maxHeight: '80vh',
  overflowY: 'auto',
  outline: 'none',
};

const ActivePostBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [sessionUsers, setSessionUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch bookings
  useEffect(() => {
    fetch(`${API_URL}/api/active-post-bookings/`)
      .then((res) => res.json())
      .then((data) => {
        const sortedData = data.sort((a, b) => b.booking_count - a.booking_count);
        setBookings(sortedData);
        setFilteredBookings(sortedData);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch bookings:', err);
        setLoading(false);
      });
  }, []);

  // Loading timer
  useEffect(() => {
    let timer;
    if (loading) {
      setLoadingSeconds(0);
      timer = setInterval(() => setLoadingSeconds((prev) => prev + 1), 1000);
    }
    return () => clearInterval(timer);
  }, [loading]);

  // Filter bookings by search query
  useEffect(() => {
    const lowerSearch = searchQuery.trim().toLowerCase();
    if (!lowerSearch) {
      setFilteredBookings(bookings);
    } else {
      const filtered = bookings.filter(post =>
        post.title?.toLowerCase().includes(lowerSearch)
      );
      setFilteredBookings(filtered);
      setCurrentPage(1); // Reset page on new search
    }
  }, [searchQuery, bookings]);

  // Pagination logic
  const totalPages = Math.ceil(filteredBookings.length / POSTS_PER_PAGE);
  const paginatedBookings = filteredBookings.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const fetchSessionUsers = async (postId) => {
    setUsersLoading(true);
    setUsersError(null);
    setSessionUsers([]);
    try {
      const resp = await fetch(`${API_URL}/api/active-post-bookings/${postId}/bookings/`);
      if (!resp.ok) throw new Error('Failed to fetch session users');
      const data = await resp.json();
      setSessionUsers(data.bookings || []);
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleRowClick = (postId) => {
    setSelectedPostId(postId);
    fetchSessionUsers(postId);
  };

  const handleCloseModal = () => {
    setSelectedPostId(null);
    setSessionUsers([]);
    setUsersError(null);
  };

  const selectedPost = bookings.find((p) => p.post_id === selectedPostId);

  if (loading) {
    return (
      <Box sx={{
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(3px)'
      }}>
        <Header1 />
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} thickness={6} sx={{ color: 'transparent' }} />
          <Typography variant="h6" sx={{ mt: 2, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.7)' }}>
            Loading Details... ({loadingSeconds} sec)
          </Typography>
        </Box>
        <Footer2 />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#000', minHeight: '100vh', pb: 10 }}>
      <Header1 />

      <Typography
        variant="h3"
        align="center"
        sx={{
          backgroundImage: 'linear-gradient(45deg, #00bfff, #ff00ff)',
          mt: 10, letterSpacing: 7, WebkitBackgroundClip: 'text', color: 'transparent'
        }}
        gutterBottom
      >
        Active Post Bookings
      </Typography>

      <Typography variant="subtitle1" align="center" sx={{ color: '#ccc', mt: 1 }}>
        Total Posts: {filteredBookings.length}
      </Typography>

      <Box sx={{ maxWidth: 900, mx: 'auto', mt: 3, px: 2 }}>
        <TextField
          fullWidth
          placeholder="Search by title..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
          size="small"
          sx={{
            backgroundColor: '#1e1e1e',
            input: { color: '#fff' },
            fieldset: { borderColor: '#444' },
            '&:hover fieldset': { borderColor: '#888' },
            '&.Mui-focused fieldset': { borderColor: '#00bfff' },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#888' }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        elevation={6}
        sx={{
          maxWidth: 900,
          margin: 'auto',
          mt: 4,
          borderRadius: 3,
          overflowX: 'auto',
          backgroundColor: '#1e1e1e',
          cursor: 'pointer',
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Post Title</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Time</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Duration (hrs)</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Bookings Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedBookings.map((post) => (
              <TableRow
                key={post.post_id}
                sx={{
                  backgroundColor: '#1a1a1a',
                  '&:nth-of-type(odd)': { backgroundColor: '#222' },
                  '&:hover': { backgroundColor: '#2a2a2a' },
                }}
                onClick={() => handleRowClick(post.post_id)}
              >
                <TableCell sx={{ color: '#ccc', maxWidth: 250, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {post.title || '—'}
                </TableCell>
                <TableCell sx={{ color: '#ccc' }}>
                  {post.selectedDate ? new Date(post.selectedDate).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell sx={{ color: '#ccc' }}>{post.startTime || '—'}</TableCell>
                <TableCell sx={{ color: '#ccc' }}>
                  {post.duration != null ? Number(post.duration).toFixed(2) : '—'}
                </TableCell>
                <TableCell sx={{ color: '#ccc' }}>{post.booking_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Stack spacing={2} sx={{ mt: 3, mb: 4 }} alignItems="center">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}

      {/* Modal */}
      <Modal
        open={!!selectedPostId}
        onClose={handleCloseModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={!!selectedPostId}>
          <Box sx={modalStyle}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" sx={{ color: '#fff' }}>
                Bookings for: {selectedPost?.title || 'Session'}
                <Typography component="span" sx={{ fontWeight: 'normal', color: '#bbb', fontSize: '1rem', ml: 1 }}>
                  ({sessionUsers.length} users)
                </Typography>
              </Typography>
              <IconButton onClick={handleCloseModal} sx={{ color: '#fff' }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {usersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress color="inherit" />
              </Box>
            ) : usersError ? (
              <Typography color="error">{usersError}</Typography>
            ) : sessionUsers.length === 0 ? (
              <Typography sx={{ color: '#ccc' }}>No users booked this session yet.</Typography>
            ) : (
              <TableContainer component={Paper} sx={{ backgroundColor: '#1e1e1e' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Surname</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Email</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Contact</TableCell>
                      <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Booked At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessionUsers.map((user) => (
                      <TableRow key={user.user_id} sx={{ '&:hover': { backgroundColor: '#2a2a2a' } }}>
                        <TableCell sx={{ color: '#ccc' }}>{user.name || '—'}</TableCell>
                        <TableCell sx={{ color: '#ccc' }}>{user.surname || '—'}</TableCell>
                        <TableCell sx={{ color: '#ccc' }}>{user.email || '—'}</TableCell>
                        <TableCell sx={{ color: '#ccc' }}>{user.contact || '—'}</TableCell>
                        <TableCell sx={{ color: '#ccc' }}>
                          {user.booked_at ? new Date(user.booked_at).toLocaleString() : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Fade>
      </Modal>

      <Footer2 />
    </Box>
  );
};

export default ActivePostBookings;
